import { useContext, useMemo, useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { BrokerPortfolioStatusesEnum, DashFeaturesEnum } from 'dash-common';
import {
  Flex,
  InputSearch,
  ModalContext,
  SearchContext,
  Select,
  Card,
  Text,
  Badge,
  Button,
  useFromNow,
  Icon,
  BulkActionsContext,
  DropDown,
  useScreenResolution,
  useCheckIfSubscriptionFeatureActiveBroker,
  FilterContext,
} from 'dash-ui';
import { useTranslation } from 'react-i18next';
import {
  GetPortfolioFolderByIdDocument,
  GetPortfoliosDocument,
  PortfolioFragment,
  useChangePortfolioStatusMutation,
  useGetPortfolioFolderItemsQuery,
} from 'gql/generated.graphql';

import EditPortfolioNameForm from './components/EditPortfolioNameForm/EditPortfolioNameForm';
import { PortfolioSortOptionsEnum } from 'pages/portfolios/PortfoliosPage/components/PortfoliosPageTopBar/PortfoliosPageFilters.constants';
import {
  PORTFOLIO_VIEW_SORT_VALUES,
  PortfolioDetailViewSortOptionsEnum,
} from '../PortfolioDetailView/PortfoliosDetailViewFilters.constants';

const OpenFormIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.b100};
  cursor: pointer;
  vertical-align: bottom;
`;

const PortfolioDetailTopBar = ({
  portfolio,
}: {
  portfolio: PortfolioFragment;
}) => {
  const { t } = useTranslation('pages');
  const theme = useTheme();
  const navigate = useNavigate();
  const { setSearchSlug } = useContext(SearchContext);
  const { setFilterState, state: filterState } = useContext(FilterContext);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const { selectedItems } = useContext(BulkActionsContext);
  const [searchValue, setSearchValue] = useState('');
  const { updatedAt, name } = portfolio;
  const fromNow = useFromNow({ short: false });
  const [changeStatus, { loading: changeStatusLoading }] =
    useChangePortfolioStatusMutation({
      refetchQueries: [GetPortfolioFolderByIdDocument, GetPortfoliosDocument],
    });
  const {
    devices: { isMobile, isTablet, isLaptop, isDesktop },
  } = useScreenResolution();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
    DashFeaturesEnum.PROPERTY_COMPARE,
  ]);

  const {
    data,
    refetch,
    loading: propertyCountLoading,
  } = useGetPortfolioFolderItemsQuery({
    variables: {
      portfolioFolderId: portfolio._id,
    },
  });

  const { totalCount } = useMemo(
    () => ({
      totalCount: data?.getPortfolioFolderItems.totalCount || 0,
    }),
    [data]
  );

  useEffect(() => {
    refetch();
  }, [selectedItems]);

  const onCompareTableOpen = () => {
    if (checkFeatureAccess()) {
      toggleModal(true, 'PropertyComparisonModal');
    }
  };

  const handleMoveFolder = () => {
    if (checkFeatureAccess()) {
      setPayload({
        selectedItems,
      });
      toggleModal(true, 'MovePropertyToFolderModal');
    }
  };

  const setFiltersState = (key: string, value: any) => {
    const isSort = key === 'sort';
    const sortOptions = isSort
      ? value.map(
          (item: string) =>
            PORTFOLIO_VIEW_SORT_VALUES[
              item as PortfolioDetailViewSortOptionsEnum
            ]
        )
      : [];
    const values = isSort ? { sortOptions, defaultValue: value } : value;

    setFilterState(key, values);
  };
  
  return (
    <Flex mb={[4, 4, 6, 6]} flexDirection="column">
      <Card flexDirection="column" w="100%" p={4}>
        {isEditFormOpen ? (
          <EditPortfolioNameForm
            value={name || ''}
            portfolioId={portfolio._id}
            onClose={() => setIsEditFormOpen(false)}
          />
        ) : (
          <Flex w="100%" justifyContent="space-between">
            <Flex flex={1}>
              <Text lg semibold>
                {portfolio.name}
                <OpenFormIcon
                  ml={2}
                  mb={1}
                  onClick={() => {
                    if (checkFeatureAccess()) {
                      setIsEditFormOpen(true);
                    }
                  }}
                  name="driveFileRenameOutline"
                  size={18}
                />
              </Text>
            </Flex>
            <Flex justifyContent="flex-end">
              <Button
                small={isLaptop || isDesktop}
                medium={isTablet || isMobile}
                square={isMobile}
                px={3}
                icon="backupTable"
                variation="outline"
                mr={2}
                onClick={() => {
                  if (checkFeatureAccess()) {
                    setPayload({ portfolioId: portfolio._id });
                    toggleModal(true, 'ManageMediaKitModal');
                  }
                }}
              >
                {t('pages:mediaKit:manageMediaKit')}
              </Button>
              <Button
                square={isMobile}
                small={isLaptop || isDesktop}
                medium={isTablet || isMobile}
                px={3}
                icon="share"
                variation="outline"
                mr={2}
                onClick={() => {
                  if (checkFeatureAccess()) {
                    toggleModal(true, 'SharePortfolioModal');
                    setPayload({ portfolio });
                  }
                }}
              >
                {t('pages:portfolioDetailsPage:labels:shareFolder')}
              </Button>
              <Button
                square={isMobile}
                small={isLaptop || isDesktop}
                medium={isTablet || isMobile}
                onClick={() => {
                  if (checkFeatureAccess()) {
                    toggleModal(true, 'RemoveFolderFromPortfolio');
                    setPayload({
                      folderId: portfolio._id,
                      onConfirm: () => navigate(-1),
                    });
                  }
                }}
                px={3}
                icon="trash"
                variation="outline"
                colorVariation="danger"
              >
                {!isMobile
                  ? isTablet
                    ? t('common:labels:delete')
                    : t('pages:portfolioDetailsPage:labels:deleteFolder')
                  : ''}
              </Button>
            </Flex>
          </Flex>
        )}

        <Flex flexWrap="wrap" flexDirection="row">
          <Flex mt={3} alignItems="center">
            <DropDown
              Anchor={({ onClick }) => (
                <Badge
                  onClick={() => {
                    if (!checkFeatureAccess()) {
                      onClick();
                    }
                    // toggle modal here
                  }}
                  loading={changeStatusLoading}
                  minHeight={32}
                  minWidth={40}
                  mr={2}
                  square
                  type={
                    portfolio.status === BrokerPortfolioStatusesEnum.PUBLIC
                      ? 'active'
                      : 'neutral'
                  }
                  iconLeft={
                    portfolio.status === BrokerPortfolioStatusesEnum.PUBLIC
                      ? 'check'
                      : 'removeCircle'
                  }
                  title={t(`pages:portfoliosPage.statuses.${portfolio.status}`)}
                />
              )}
              items={Object.values(BrokerPortfolioStatusesEnum).map((item) => ({
                key: item,
                children: t(`pages:portfoliosPage.statuses.${item}`),
                onClick: () =>
                  changeStatus({
                    variables: {
                      portfolioId: portfolio._id,
                      status: item as any,
                    },
                  }),
              }))}
            />
          </Flex>
          <Flex mt={3}>
            <Badge
              mr={2}
              square
              type="neutral"
              bgColor={theme.colors.b10}
              iconLeft="editRoad"
              iconRight="chevronRight"
              title={fromNow(updatedAt)}
            />
          </Flex>
          <Flex mt={3}>
            <Badge
              mr={2}
              square
              type="inactive"
              iconLeft="visibility"
              title={t('pages:portfolioDetailsPage:labels:views', {
                number: portfolio.viewsCount,
              })}
            />
          </Flex>
          <Flex mt={3}>
            <Badge
              loading={propertyCountLoading}
              mr={2}
              minHeight={32}
              minWidth={40}
              square
              type="inactive"
              title={t('pages:portfolioDetailsPage:labels:properties', {
                number: totalCount,
              })}
            />
          </Flex>
        </Flex>
      </Card>
      <Flex
        mt={[4, 4, 6, 6]}
        w="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <InputSearch
          mr={2}
          placeholder={
            (isMobile
              ? t('portfoliosPage.portfoliosPropertySearchPlaceholderShort')
              : t('portfoliosPage.portfoliosPropertySearchPlaceholder')) || ''
          }
          w="100%"
          value={searchValue}
          maxWidth={400}
          onClear={() => {
            setSearchValue('');
            setSearchSlug('');
          }}
          onChange={(e) => {
            setSearchSlug(e.target.value);
            setSearchValue(e.target.value);
          }}
        />
        <Flex>
          {!isMobile && (
            <Flex>
              {Object.keys(selectedItems).length ? (
                <>
                  <Button onClick={onCompareTableOpen} icon="compareArrows">
                    {t('common:labels.compare')}
                  </Button>
                  <Button
                    onClick={handleMoveFolder}
                    mx={2}
                    variation="outline"
                    icon="driveFileMove"
                  >
                    {isTablet
                      ? t('portfolioDetailsPage.labels.move')
                      : t('portfolioDetailsPage.labels.moveProperty')}
                  </Button>
                </>
              ) : null}
            </Flex>
          )}
          <Select
            menuPlacementHorizontal={isMobile ? 'right' : 'left'}
            menuPlacement="bottom"
            isSearchable={false}
            w={[86, 160, 160, 160]}
            withCheckbox
            leftIcon={null}
            value={filterState?.sort?.defaultValue}
            isMulti
            isClearable
            options={Object.values(PortfolioSortOptionsEnum).map((item) => ({
              label: t(`common:sortOptions.${item}`),
              value: item,
            }))}
            onChanged={(_, values) => setFiltersState('sort', values)}
            placeholder={
              (isMobile
                ? t('portfoliosPage.sort')
                : t('portfoliosPage.sortBy')) || ''
            }
          />
        </Flex>
      </Flex>
      {isMobile && (
        <Flex mt={3} w="100%" flex={1}>
          <Flex flex={1} mr={3}>
            {Object.keys(selectedItems).length ? (
              <Button
                w="100%"
                px={3}
                icon="compareArrows"
                onClick={onCompareTableOpen}
              >
                {t('common:labels.compare')}
              </Button>
            ) : null}
          </Flex>
          <Flex flex={1}>
            {Object.keys(selectedItems).length ? (
              <Button
                px={3}
                variation="outline"
                icon="driveFileMove"
                onClick={handleMoveFolder}
              >
                {t('portfolioDetailsPage.labels.moveProperty')}
              </Button>
            ) : null}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default PortfolioDetailTopBar;
